import React from "react";
import { SectionHeading } from "../../components/SectionHeading";
import { useTranslation } from "react-i18next";
import { FaTaxi } from "react-icons/fa";
import { FaRegClock } from "react-icons/fa6";
import { IoLockClosedOutline, IoPerson } from 'react-icons/io5';
import PageLayout from "../../components/PageLayout";

// Types
interface CardProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

// Subcomponents
const HeroSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 my-6 md:my-10">
      <div className="flex justify-center md:col-span-3 order-1">
        <img
          src="/image/passenger_car.png"
          alt="Passenger Car"
          className="w-[60%] md:w-[80%] h-auto mx-auto"
        />
      </div>
      <div className="flex flex-col justify-center md:col-span-2 text-center md:text-left order-2 mt-6 md:mt-0">
        <SectionHeading>{t('PassengerHero1')}</SectionHeading>
        <br/>
        <p className="text-sm md:text-base">{t('PassengerHero2')}</p>
        <br/>
        <p className="text-sm md:text-base">{t('PassengerHero3')}</p>
        <br/>
        <ul className="list-disc list-outside pl-5 text-sm md:text-base mx-auto md:mx-0 text-left max-w-xs md:max-w-none">
          <li>{t('PassengerHero4')}</li>
          <li>{t('PassengerHero5')}</li>
          <li>{t('PassengerHero6')}</li>
        </ul>
      </div>
    </div>
  );
};

const FeatureCard: React.FC<CardProps> = ({ title, description, icon }) => {
  return (
    <div className="flex flex-col bg-gradient-to-t from-[#EDA558] to-[#FFD23E] rounded-lg overflow-hidden shadow-lg p-4 md:p-6 h-full">
      <div className="text-black font-bold text-lg md:text-xl mb-2 h-auto md:h-12 flex items-center justify-center text-center">
        {title}
      </div>
      {icon && (
        <div className="flex justify-center items-center my-3 md:my-4 h-16 md:h-20">
          {icon}
        </div>
      )}
      <p className="text-black text-sm md:text-base text-center flex-grow">
        {description}
      </p>
    </div>
  );
};

const RestAssured: React.FC = () => {
  const { t } = useTranslation();
  const features: CardProps[] = [
    {
      title: t('PassengerRestAssuredCard1'),
      description: t('PassengerRestAssuredCard1Note'),
      icon: <FaRegClock size={40} className="md:text-[60px]" color="black"/>
    },
    {
      title: t('PassengerRestAssuredCard2'),
      description: t('PassengerRestAssuredCard2Note'),
      icon: <FaTaxi size={40} className="md:text-[60px]" color="black"/>
    },
    {
      title: t('PassengerRestAssuredCard3'),
      description: t('PassengerRestAssuredCard3Note'),
      icon: <IoLockClosedOutline size={40} className="md:text-[60px]" color="black"/>
    },
    {
      title: t('PassengerRestAssuredCard4'),
      description: t('PassengerRestAssuredCard4Note'),
      icon: <IoPerson size={40} className="md:text-[60px]" color="black"/>
    }
  ];

  return (
    <div className="my-6 md:my-10 text-center px-4 md:px-0">
      <SectionHeading>{t('PassengerRestAssured1')}</SectionHeading>
      <p className="m-4 md:m-8 text-sm md:text-base">{t('PassengerRestAssured2')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 justify-center">
        {features.map((feature, index) => (
          <FeatureCard
            key={index}
            title={feature.title}
            description={feature.description}
            icon={feature.icon}
          />
        ))}
      </div>
    </div>
  );
};

const ServiceSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 my-6 md:my-10">
      <div className="flex flex-col justify-center md:col-span-2 text-center md:text-left order-2 md:order-1 mb-4 md:mb-0">
        <SectionHeading>{t('PassengerService1')}</SectionHeading>
        <br/>
        <p className="text-sm md:text-base">
          {t('PassengerService2')}
        </p>
      </div>
      <div className="flex justify-center md:col-span-2 order-1 md:order-2 mb-4 md:mb-0">
        <img
          src="/image/car_with_waves_removebg.png"
          alt="Service Map"
          className="w-[50%] md:w-[80%] h-auto mx-auto"
        />
      </div>
    </div>
  );
};

const PassengerPage: React.FC = () => {
  return (
    <PageLayout>
      <div className="flex justify-center items-center w-full px-4 sm:px-0 pt-20">
        <main className="flex flex-col items-center w-full sm:w-[90%] md:w-[85%] lg:w-[80%]">
          <HeroSection />
          <RestAssured />
          <ServiceSection />
        </main>
      </div>
    </PageLayout>
  );
};

export default PassengerPage;